import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Eye, EyeOff, CheckCircle2, Loader2, UserCheck, User, Phone, MapPin, Building2, Calendar, CreditCard, Heart, AlertCircle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ActivateAccountPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [activating, setActivating] = useState(false);
  const [activated, setActivated] = useState(false);
  const [error, setError] = useState(null);
  const [employeeInfo, setEmployeeInfo] = useState(null);
  const [step, setStep] = useState(1); // 1: Password, 2: Profile, 3: Complete
  
  // Password fields
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  
  // Profile fields
  const [profile, setProfile] = useState({
    phone: '',
    address: '',
    date_of_birth: '',
    ic_number: '',
    gender: '',
    marital_status: '',
    nationality: 'malaysian',
    race: '',
    religion: '',
    bank_name: '',
    bank_account: '',
    emergency_contact_name: '',
    emergency_contact_phone: '',
    emergency_contact_relationship: ''
  });

  useEffect(() => {
    const fetchActivationInfo = async () => {
      try {
        const response = await axios.get(`${API}/api/hr/activate/${token}`);
        setEmployeeInfo(response.data);
        // Pre-fill any existing data
        if (response.data) {
          setProfile(prev => ({
            ...prev,
            phone: response.data.phone || '',
            address: response.data.address || '',
            date_of_birth: response.data.date_of_birth?.split('T')[0] || '',
            ic_number: response.data.ic_number || '',
            gender: response.data.gender || '',
            marital_status: response.data.marital_status || '',
            nationality: response.data.nationality || 'malaysian',
            race: response.data.race || '',
            religion: response.data.religion || '',
            bank_name: response.data.bank_name || '',
            bank_account: response.data.bank_account || '',
            emergency_contact_name: response.data.emergency_contact_name || '',
            emergency_contact_phone: response.data.emergency_contact_phone || '',
            emergency_contact_relationship: response.data.emergency_contact_relationship || ''
          }));
        }
      } catch (err) {
        setError(err.response?.data?.detail || 'Invalid or expired activation link');
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      fetchActivationInfo();
    }
  }, [token]);

  const handlePasswordSubmit = (e) => {
    e.preventDefault();
    
    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    setStep(2);
  };

  const handleProfileChange = (field, value) => {
    setProfile(prev => ({ ...prev, [field]: value }));
  };

  const handleActivate = async (e) => {
    e.preventDefault();
    
    setActivating(true);
    try {
      // Send password and profile data together
      await axios.post(`${API}/api/hr/activate/${token}`, { 
        password,
        profile_data: profile
      });
      setActivated(true);
      setStep(3);
      toast.success('Account activated and profile updated!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to activate account');
    } finally {
      setActivating(false);
    }
  };

  const skipProfile = async () => {
    setActivating(true);
    try {
      await axios.post(`${API}/api/hr/activate/${token}`, { password });
      setActivated(true);
      setStep(3);
      toast.success('Account activated successfully!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to activate account');
    } finally {
      setActivating(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-slate-800 border-slate-700">
          <CardHeader className="text-center">
            <div className="mx-auto w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mb-4">
              <AlertCircle className="h-8 w-8 text-red-500" />
            </div>
            <CardTitle className="text-red-400">Activation Failed</CardTitle>
            <CardDescription className="text-slate-400">{error}</CardDescription>
          </CardHeader>
          <CardContent className="text-center">
            <Button onClick={() => navigate('/login')} variant="outline">
              Go to Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Step 3: Success
  if (step === 3 || activated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-slate-800 border-slate-700">
          <CardHeader className="text-center">
            <div className="mx-auto w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mb-4">
              <CheckCircle2 className="h-8 w-8 text-emerald-500" />
            </div>
            <CardTitle className="text-emerald-400">Welcome Aboard!</CardTitle>
            <CardDescription className="text-slate-400">
              Your account has been activated and your profile is complete. 
              Your HR team has been notified. You can now login to access your employee dashboard.
            </CardDescription>
          </CardHeader>
          <CardContent className="text-center space-y-4">
            <div className="p-4 bg-slate-700/50 rounded-lg text-left">
              <p className="text-sm text-slate-400">
                <span className="text-emerald-400 font-medium">What's next?</span>
              </p>
              <ul className="text-sm text-slate-400 mt-2 space-y-1">
                <li>• View your payslips</li>
                <li>• Apply for leave</li>
                <li>• Submit expense claims</li>
                <li>• Update your profile anytime</li>
              </ul>
            </div>
            <Button onClick={() => navigate('/login')} className="w-full bg-emerald-600 hover:bg-emerald-700">
              Login to Dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center p-4">
      <Card className="w-full max-w-2xl bg-slate-800 border-slate-700">
        <CardHeader className="text-center">
          <div className="mx-auto w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mb-4">
            <UserCheck className="h-8 w-8 text-emerald-500" />
          </div>
          <CardTitle className="text-white">
            {step === 1 ? 'Activate Your Account' : 'Complete Your Profile'}
          </CardTitle>
          <CardDescription className="text-slate-400">
            {step === 1 
              ? `Welcome, ${employeeInfo?.name}! Set your password to get started.`
              : 'Help us complete your employee record. You can update this later.'}
          </CardDescription>
          
          {/* Progress indicator */}
          <div className="flex items-center justify-center mt-4 space-x-2">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${step >= 1 ? 'bg-emerald-600 text-white' : 'bg-slate-700 text-slate-400'}`}>1</div>
            <div className={`w-16 h-1 ${step >= 2 ? 'bg-emerald-600' : 'bg-slate-700'}`}></div>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${step >= 2 ? 'bg-emerald-600 text-white' : 'bg-slate-700 text-slate-400'}`}>2</div>
            <div className={`w-16 h-1 ${step >= 3 ? 'bg-emerald-600' : 'bg-slate-700'}`}></div>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${step >= 3 ? 'bg-emerald-600 text-white' : 'bg-slate-700 text-slate-400'}`}>3</div>
          </div>
        </CardHeader>
        
        <CardContent>
          {/* Step 1: Password */}
          {step === 1 && (
            <>
              <div className="mb-6 p-4 bg-slate-700/50 rounded-lg">
                <h3 className="text-sm font-medium text-slate-300 mb-2">Your Details:</h3>
                <div className="grid grid-cols-2 gap-2 text-sm text-slate-400">
                  <p><span className="text-slate-500">Email:</span> {employeeInfo?.email}</p>
                  <p><span className="text-slate-500">Employee ID:</span> {employeeInfo?.employee_id}</p>
                  {employeeInfo?.position && (
                    <p><span className="text-slate-500">Position:</span> {employeeInfo?.position}</p>
                  )}
                  {employeeInfo?.department && (
                    <p><span className="text-slate-500">Department:</span> {employeeInfo?.department}</p>
                  )}
                </div>
              </div>

              <form onSubmit={handlePasswordSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="password" className="text-slate-300">Password</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Enter your password"
                      className="bg-slate-700 border-slate-600 text-white pr-10"
                      required
                      minLength={6}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirmPassword" className="text-slate-300">Confirm Password</Label>
                  <Input
                    id="confirmPassword"
                    type={showPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm your password"
                    className="bg-slate-700 border-slate-600 text-white"
                    required
                    minLength={6}
                  />
                </div>

                <Button type="submit" className="w-full bg-emerald-600 hover:bg-emerald-700">
                  Continue to Profile Setup
                </Button>
              </form>
            </>
          )}

          {/* Step 2: Profile */}
          {step === 2 && (
            <form onSubmit={handleActivate} className="space-y-6">
              {/* Personal Information */}
              <div>
                <h3 className="text-sm font-semibold text-emerald-400 mb-3 flex items-center">
                  <User className="h-4 w-4 mr-2" /> Personal Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-slate-300">IC Number / Passport</Label>
                    <Input
                      value={profile.ic_number}
                      onChange={(e) => handleProfileChange('ic_number', e.target.value)}
                      placeholder="e.g., 901231-10-1234"
                      className="bg-slate-700 border-slate-600 text-white"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-300">Date of Birth</Label>
                    <Input
                      type="date"
                      value={profile.date_of_birth}
                      onChange={(e) => handleProfileChange('date_of_birth', e.target.value)}
                      className="bg-slate-700 border-slate-600 text-white"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-300">Gender</Label>
                    <Select value={profile.gender} onValueChange={(v) => handleProfileChange('gender', v)}>
                      <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                        <SelectValue placeholder="Select gender" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="male">Male</SelectItem>
                        <SelectItem value="female">Female</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-300">Marital Status</Label>
                    <Select value={profile.marital_status} onValueChange={(v) => handleProfileChange('marital_status', v)}>
                      <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                        <SelectValue placeholder="Select status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="single">Single</SelectItem>
                        <SelectItem value="married">Married</SelectItem>
                        <SelectItem value="divorced">Divorced</SelectItem>
                        <SelectItem value="widowed">Widowed</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-300">Race</Label>
                    <Select value={profile.race} onValueChange={(v) => handleProfileChange('race', v)}>
                      <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                        <SelectValue placeholder="Select race" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="malay">Malay</SelectItem>
                        <SelectItem value="chinese">Chinese</SelectItem>
                        <SelectItem value="indian">Indian</SelectItem>
                        <SelectItem value="others">Others</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-300">Religion</Label>
                    <Select value={profile.religion} onValueChange={(v) => handleProfileChange('religion', v)}>
                      <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                        <SelectValue placeholder="Select religion" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="islam">Islam</SelectItem>
                        <SelectItem value="buddhism">Buddhism</SelectItem>
                        <SelectItem value="christianity">Christianity</SelectItem>
                        <SelectItem value="hinduism">Hinduism</SelectItem>
                        <SelectItem value="others">Others</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Contact Information */}
              <div>
                <h3 className="text-sm font-semibold text-emerald-400 mb-3 flex items-center">
                  <Phone className="h-4 w-4 mr-2" /> Contact Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-slate-300">Phone Number</Label>
                    <Input
                      value={profile.phone}
                      onChange={(e) => handleProfileChange('phone', e.target.value)}
                      placeholder="e.g., +60123456789"
                      className="bg-slate-700 border-slate-600 text-white"
                    />
                  </div>
                  <div className="space-y-2 md:col-span-2">
                    <Label className="text-slate-300">Address</Label>
                    <Input
                      value={profile.address}
                      onChange={(e) => handleProfileChange('address', e.target.value)}
                      placeholder="Your full address"
                      className="bg-slate-700 border-slate-600 text-white"
                    />
                  </div>
                </div>
              </div>

              {/* Bank Information */}
              <div>
                <h3 className="text-sm font-semibold text-emerald-400 mb-3 flex items-center">
                  <CreditCard className="h-4 w-4 mr-2" /> Bank Information (for salary)
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-slate-300">Bank Name</Label>
                    <Select value={profile.bank_name} onValueChange={(v) => handleProfileChange('bank_name', v)}>
                      <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                        <SelectValue placeholder="Select bank" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="maybank">Maybank</SelectItem>
                        <SelectItem value="cimb">CIMB Bank</SelectItem>
                        <SelectItem value="publicbank">Public Bank</SelectItem>
                        <SelectItem value="rhb">RHB Bank</SelectItem>
                        <SelectItem value="hongLeong">Hong Leong Bank</SelectItem>
                        <SelectItem value="ambank">AmBank</SelectItem>
                        <SelectItem value="bankislam">Bank Islam</SelectItem>
                        <SelectItem value="bsn">BSN</SelectItem>
                        <SelectItem value="ocbc">OCBC Bank</SelectItem>
                        <SelectItem value="uob">UOB Bank</SelectItem>
                        <SelectItem value="others">Others</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-300">Bank Account Number</Label>
                    <Input
                      value={profile.bank_account}
                      onChange={(e) => handleProfileChange('bank_account', e.target.value)}
                      placeholder="Your account number"
                      className="bg-slate-700 border-slate-600 text-white"
                    />
                  </div>
                </div>
              </div>

              {/* Emergency Contact */}
              <div>
                <h3 className="text-sm font-semibold text-emerald-400 mb-3 flex items-center">
                  <Heart className="h-4 w-4 mr-2" /> Emergency Contact
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label className="text-slate-300">Contact Name</Label>
                    <Input
                      value={profile.emergency_contact_name}
                      onChange={(e) => handleProfileChange('emergency_contact_name', e.target.value)}
                      placeholder="Full name"
                      className="bg-slate-700 border-slate-600 text-white"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-300">Contact Phone</Label>
                    <Input
                      value={profile.emergency_contact_phone}
                      onChange={(e) => handleProfileChange('emergency_contact_phone', e.target.value)}
                      placeholder="Phone number"
                      className="bg-slate-700 border-slate-600 text-white"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-300">Relationship</Label>
                    <Select value={profile.emergency_contact_relationship} onValueChange={(v) => handleProfileChange('emergency_contact_relationship', v)}>
                      <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="spouse">Spouse</SelectItem>
                        <SelectItem value="parent">Parent</SelectItem>
                        <SelectItem value="sibling">Sibling</SelectItem>
                        <SelectItem value="child">Child</SelectItem>
                        <SelectItem value="friend">Friend</SelectItem>
                        <SelectItem value="others">Others</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between pt-4">
                <Button type="button" variant="ghost" onClick={skipProfile} className="text-slate-400">
                  Skip for now
                </Button>
                <Button type="submit" disabled={activating} className="bg-emerald-600 hover:bg-emerald-700">
                  {activating ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Activating...
                    </>
                  ) : (
                    'Complete Activation'
                  )}
                </Button>
              </div>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
